import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faChampagneGlasses } from '@fortawesome/pro-duotone-svg-icons';

import {
	Button,
	Form,
	Input,
	Select,
	Switch,
	Table,
	Tooltip,
	Typography,
	notification,
} from 'antd';

import { txt } from '../../../locales/i18n';
import { SharedTable } from '../../shared/table';
import * as AdvertisementService from '../../../services/bb-api/advertisement';

type Advertisement = {
	id: number;
	advertiser: string;
	trackLink: string;
	pageLink: string;
	active: boolean;
	teaser: string;
	imageSmall: string;
	imageLarge: string;
};

type AdvertisementQuery = {};

export const AdvertisementsPage = () => {
	const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
	const [advertisers, setAdvertisers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [queryForm] = Form.useForm();

	useEffect(() => {
		searchAdvertisements();
	}, []);

	const searchAdvertisements = async (values: AdvertisementQuery = {}) => {
		setLoading(true);

		try {
			const advertisements: Advertisement[] = await AdvertisementService.search(values);
			setAdvertisements(advertisements);

			const advertisers = await AdvertisementService.getAdvertisers();
			setAdvertisers(advertisers);
		} catch (error) {
			console.error(error);
		}

		setLoading(false);
	};

	const renderImage = (advertisement: Advertisement) => {
		const classNames = ['advertisement-image'];
		if (advertisement.active === false) classNames.push('inactive');

		return (
			<img src={advertisement.imageSmall} alt="ad preview" className={classNames.join(' ')} />
		);
	};

	const renderTeaser = (teaser: string) => {
		return <Typography.Paragraph ellipsis={{ rows: 3 }}>{teaser}</Typography.Paragraph>;
	};

	const renderAdvertiser = (advertisement: Advertisement) => {
		const toggleActive = async (value: boolean) => {
			try {
				await AdvertisementService.toggleActive(advertisement.id, value);
				const message = txt('notification.update successful');
				notification.success({ message });
				queryForm.submit();
			} catch (error) {
				const message = txt('notification.update failed');
				notification.error({ message });
			}
		};

		return (
			<>
				<Typography.Title level={4}>
					<Tooltip title={txt('global.open')}>
						<a href={'/advertisements/' + advertisement.id}>
							{advertisement.advertiser}
						</a>
					</Tooltip>
				</Typography.Title>

				<Tooltip
					title={advertisement.active ? txt('global.active') : txt('global.inactive')}
					placement="right"
				>
					<Switch onClick={toggleActive} checked={advertisement.active} />
				</Tooltip>
			</>
		);
	};

	const renderRegion = (region: string) => {
		const flags: any = {
			Denmark: 'https://filer.boblberg.dk/Assets/Img/flags/da.png',
			Sweden: 'https://filer.boblberg.dk/Assets/Img/flags/sv.png',
			Germany: 'https://filer.boblberg.dk/Assets/Img/flags/de.png',
		};

		return <img className="region-flag" src={flags[region]} alt="region flag" />;
	};

	const renderNewYear = (newYear: boolean) => {
		if (newYear) {
			return (
				<Tooltip title="Nytårsvenner">
					<FontAwesomeIcon icon={faChampagneGlasses} size="xl" color="dodgerblue" />
				</Tooltip>
			);
		}

		return null;
	};

	return (
		<AdvertisementsPageStyled>
			<div className="header-container">
				<Form onFinish={searchAdvertisements} layout="inline" form={queryForm}>
					<Tooltip title={txt('global.search')}>
						<Form.Item name="search">
							<Input allowClear placeholder={txt('global.search')} />
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.advertiser')}>
						<Form.Item name="advertiser">
							<Select
								allowClear
								loading={loading}
								dropdownMatchSelectWidth={false}
								placeholder={txt('global.advertiser')}
							>
								{advertisers.map((advertiser) => (
									<Select.Option key={advertiser} value={advertiser}>
										{advertiser}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.active')}>
						<Form.Item name="active">
							<Select
								allowClear
								placeholder={txt('global.all')}
								dropdownMatchSelectWidth={false}
								loading={loading}
							>
								<Select.Option value="active">{txt('global.active')}</Select.Option>
								<Select.Option value="inactive">
									{txt('global.inactive')}
								</Select.Option>
							</Select>
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.country')}>
						<Form.Item name="region">
							<Select
								allowClear
								placeholder={txt('countries.all')}
								dropdownMatchSelectWidth={false}
								loading={loading}
							>
								<Select.Option value="Denmark">
									{txt('countries.denmark')}
								</Select.Option>

								<Select.Option value="Sweden">
									{txt('countries.sweden')}
								</Select.Option>

								<Select.Option value="Germany">
									{txt('countries.germany')}
								</Select.Option>
							</Select>
						</Form.Item>
					</Tooltip>

					<Tooltip title="Nytårsvenner">
						<Form.Item name="newYear">
							<Select
								allowClear
								loading={loading}
								placeholder="Nytårsvenner"
								dropdownMatchSelectWidth={false}
							>
								<Select.Option value="yes">{txt('global.yes')}</Select.Option>
								<Select.Option value="no">{txt('global.no')}</Select.Option>
							</Select>
						</Form.Item>
					</Tooltip>

					<Button type="primary" htmlType="submit" loading={loading}>
						{txt('global.search')}
					</Button>
				</Form>

				<Tooltip title={txt('global.create')}>
					<Button type="link" href="/advertisements/$">
						<FontAwesomeIcon icon={faPlus} />
					</Button>
				</Tooltip>
			</div>

			<SharedTable
				rowKey="id"
				cursor="default"
				dataSource={advertisements}
				loading={loading}
				rowClassName={({ active }) => (active ? '' : 'inactive')}
			>
				<Table.Column title={txt('global.image')} render={renderImage} />
				<Table.Column
					width={256}
					render={renderAdvertiser}
					title={txt('global.advertiser')}
				/>

				<Table.Column
					dataIndex="teaser"
					title={txt('global.teaser')}
					render={renderTeaser}
				/>

				<Table.Column
					width={64}
					align="center"
					dataIndex="newYear"
					render={renderNewYear}
					title={
						<Tooltip title="Nytårsvenner">
							<FontAwesomeIcon icon={faChampagneGlasses} />
						</Tooltip>
					}
				/>

				<Table.Column
					width={64}
					align="center"
					dataIndex="region"
					title={txt('global.country')}
					render={renderRegion}
				/>
			</SharedTable>
		</AdvertisementsPageStyled>
	);
};

const AdvertisementsPageStyled = styled.main`
	.header-container {
		margin: 1rem;
		display: flex;
		align-items: center;

		form:first-child {
			margin-right: auto;
		}
	}

	.links-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.advertisement-image {
		width: 100%;
		object-fit: contain;
		transition: filter 350ms;
		max-height: 120px;

		&.inactive {
			filter: grayscale(80%);
		}
	}

	td:first-child {
		margin: 0;
		padding: 0;
	}

	.region-flag {
		width: 32px;
	}

	tr {
		&:hover {
			.advertisement-image {
				filter: grayscale(0%);
			}
		}

		&.inactive {
			td:first-child {
				border-left: 3px solid grey;
			}

			&:nth-child(even) {
				background-color: #f0f0f0;
			}

			&:nth-child(odd) {
				background-color: #f6f6f6;
			}
		}
	}
`;
